/**
 * The signed-in session, kept in localStorage so a reload stays signed in.
 *
 * Lives below `api.ts` (which reads the token for every request) and the auth
 * store (which writes it), so neither has to import the other.
 */

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  pictureUrl?: string | null;
  createdAt: string;
};

export type Session = {
  accessToken: string;
  /** ISO timestamp; the API rejects the token after this. */
  expiresAt: string;
  user: SessionUser;
};

const SESSION_KEY = 'gem.session';
/** Remembers that this browser has signed in before, to land on "Sign in" instead of "Create account". */
const RETURNING_KEY = 'gem.returning';
/** Other per-account state that must not leak into the next account on this browser. */
const ACCOUNT_SCOPED_KEYS = ['gem.groups'];

export function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (!session.accessToken || Date.parse(session.expiresAt) <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function writeSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    localStorage.setItem(RETURNING_KEY, '1');
  } catch {
    // Storage unavailable (private mode): the session lasts until reload.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    ACCOUNT_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing persisted to clear.
  }
}

export function isReturningVisitor(): boolean {
  try {
    return localStorage.getItem(RETURNING_KEY) === '1';
  } catch {
    return false;
  }
}

export function getAccessToken(): string | null {
  return readSession()?.accessToken ?? null;
}

/**
 * Drops the session and hard-navigates to the sign-in page. A full page load
 * (rather than a router push) is deliberate: it empties every in-memory store,
 * so nothing from this account is left behind for the next one.
 */
export function endSession(path = '/login'): void {
  clearSession();
  window.location.replace(path);
}
